import { useState } from 'react';
import styled from 'styled-components';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { ProcessStatus } from 'constants/constants';
import { useTranslation } from 'react-i18next';
import { PCRModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import { A } from 'styles/Styles.styled';
import { Option } from 'types/types';
import { isPcrExpired } from 'util/utils';
import { DownloadButton, PcrListItem, PcrListItemHeader } from '../styled';
import PcrSelectorDialogFormCategorySelector from './PcrSelectorDialogFormCategorySelector';
import MyRadioButton from 'components/controls/MyRadioButton';

interface TProps {
  selectedPcr: PCRModel | null;
  onSelectedPcrChange(pcr: PCRModel | null): void;
  productCategoryList: Option[];
  productCategory: Option | undefined;
  pcrList: PCRModel[];
  status: ProcessStatus;
  onProductCategorySelect: (value: Option | undefined) => void;
  disableCategorySelector: boolean;
}

const PcrSelectorDialogForm = ({
  productCategoryList,
  productCategory,
  pcrList,
  status,
  onProductCategorySelect,
  disableCategorySelector,
  selectedPcr,
  onSelectedPcrChange,
}: TProps) => {
  const { t } = useTranslation();
  const [nowEpoch] = useState<number>(Date.now());

  return (
    <>
      <div>
        <SmartLabel label={'Category'} />
        {!disableCategorySelector ? (
          <PcrSelectorDialogFormCategorySelector
            name={'productCategory'}
            options={productCategoryList}
            value={productCategory}
            isClearable
            onChange={(value: any) => onProductCategorySelect(value)}
            placeholder={'Type a category name for a quick search'}
          />
        ) : (
          <PcrSelectorDialogFormCategorySelector
            name={'productCategory'}
            options={[]}
            value={{ label: 'Construction products', value: '' }}
            isClearable
            onChange={() => {}}
            isDisabled
          />
        )}
      </div>

      <ListContainer>
        {status === ProcessStatus.Fetching && (
          <div style={{ padding: '1rem', fontStyle: 'italic' }}>{t('epdWizard.wizardStep1.pcrMenu.messages.fetchList')}</div>
        )}

        {status === ProcessStatus.Success &&
          (!!pcrList?.length ? (
            pcrList.map((item) => {
              const isExpired = isPcrExpired(item.validUntil, nowEpoch);
              const isDisabled = isExpired;
              const isSelected = selectedPcr?.id === item.id;

              return (
                <PcrListItem key={item.id} $selected={isSelected} $disabled={isDisabled}>
                  <PcrListItemHeader $disabled={isDisabled}>
                    <span
                      style={{ fontWeight: 'bold', marginRight: '0.5rem' }}
                      onClick={(ev) => {
                        if (process.env.REACT_APP_DEBUG && ev.ctrlKey) {
                          ev.preventDefault();
                          ev.stopPropagation();
                          onSelectedPcrChange(item);
                        }
                      }}
                    >
                      {item.fullName}
                    </span>

                    <MyRadioButton
                      inputId={item.id}
                      name="pcrItem"
                      value={item.id!}
                      checked={isSelected}
                      onChange={() => onSelectedPcrChange(item)}
                      disabled={isDisabled}
                    />
                  </PcrListItemHeader>

                  {item.cpCs && (
                    <div style={{ opacity: isDisabled ? 0.5 : undefined }}>
                      {item.cpCs.map((cpc) => (
                        <div style={{ paddingTop: '0.5rem' }} key={cpc.id}>
                          <em>{cpc.code}</em> <span>{cpc.description}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.documents &&
                    item.documents.length > 0 &&
                    item.documents.map(
                      (file, index) =>
                        file && (
                          <div key={index}>
                            <div style={{ paddingTop: '0.5rem', opacity: isDisabled ? 0.5 : undefined }}>
                              <span>{file?.name || t('epdWizard.wizardStep1.pcrMenu.placeholderImage')}</span>
                            </div>
                            <A target="_blank" href={FileService.getImageUrl(file?.id || '')}>
                              <DownloadButton label="Download file" icon="pi pi-download" />
                            </A>
                          </div>
                        )
                    )}

                  {isExpired && <ExpiredTag>Expired</ExpiredTag>}
                </PcrListItem>
              );
            })
          ) : (
            <div style={{ padding: '1rem', fontStyle: 'italic' }}>
              {'No items in this category or category is not selected'}
            </div>
          ))}
      </ListContainer>
    </>
  );
};

export default PcrSelectorDialogForm;

const ListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

const ExpiredTag = styled.div`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  padding: 2px 4px;
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
  color: ${(props) => props.theme.colors.statusLabelRedText};
  font-size: 10px;
  font-weight: 500;
  line-height: 1.25;
  text-transform: uppercase;
`;
